import { useEffect, useRef, useState } from "react"
import type { FormEvent } from "react"
import { Link } from "react-router-dom"
import { ApiError, api } from "../api/client.ts"
import type { Case } from "../api/types.ts"
import { useApiQuery } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { beep } from "../lib/beep.ts"
import { formatTracking } from "../lib/tracking.ts"
import type { ArrivalSave } from "../lib/arrival-entry.ts"
import { caseColumns } from "../components/case-columns.tsx"
import { useCaseDetailPanel } from "../components/CaseDetailPanel.tsx"
import { ArrivalEntryCard } from "../components/scan/ArrivalEntryCard.tsx"
import type { OrphanRow } from "../components/scan/OrphanLinkPanel.tsx"
import { useOrphanLink } from "../components/scan/useOrphanLink.tsx"
import { Card, CardHeader, DataTable, EmptyState, Field, Input, PageHeader, type Column } from "../ui/index.ts"

type Banner = { tone: "ok" | "warn" | "error"; text: string }

/** 정체를 모르는 상자. 접수된 건이 없는데 사가와·야마토 운송장 형식일 때 서버가 적는다. */
type Orphan = OrphanRow

/** 찍은 결과. 입고된 건이거나 무적화물이거나 둘 중 하나다. */
type ScanResponse = { case?: Case; orphan?: Orphan; created?: boolean }

/**
 * 창고에서 한 손으로 쓰는 화면.
 *
 * 입력창이 늘 포커스를 되찾고, 결과를 큰 색 배너와 소리로 알린다 —
 * 상자를 든 채로는 화면의 작은 글씨를 읽지 못한다.
 *
 * 전용 스캐너 앱이 읽은 번호도 입력창에 친 번호와 똑같이 `submitTracking` 한 곳으로
 * 들어간다(조회·입고 처리·배너·소리·중량 단계가 같다).
 */
export function ScanPage() {
  const { t, locale } = useI18n()
  const inputRef = useRef<HTMLInputElement>(null)
  const [banner, setBanner] = useState<Banner | null>(null)
  const [current, setCurrent] = useState<Case | null>(null)
  const [busy, setBusy] = useState(false)

  const today = useApiQuery<{ items: Case[] }>("/api/scan/today")
  // 조건 없이 부른다 — 아직 안 이은 상자, 최근 순. 전용 화면(/orphans)도 같은 라우트를 쓴다.
  const orphans = useApiQuery<{ items: Orphan[] }>("/api/scan/orphans")
  const rows = today.data?.items ?? []
  const detail = useCaseDetailPanel({ onChanged: () => void today.reload(), rows })

  /** 잇기는 전용 화면과 같은 훅을 쓴다(`components/scan/useOrphanLink.tsx`). */
  const linking = useOrphanLink({
    onLinked: (linked) => {
      // 이어진 건은 입고완료가 됐다 — 오늘 스캔 목록에 나타나고 무적화물에서는 빠진다.
      setBanner({ tone: "ok", text: t("orphan.linked", { orderNo: linked.orderNo }) })
      void orphans.reload()
      void today.reload()
    },
  })

  /**
   * 입력창으로 포커스를 돌려 달라는 요청. 바코드 스캐너는 포커스가 있는 칸에만 번호를 쏜다.
   *
   * 요청 중에는 입력칸이 `disabled` 라 그 자리에서 `focus()` 를 불러도 아무 일도 일어나지 않는다.
   * 그래서 "돌려 달라"만 표시해 두고, 화면이 다시 그려져 칸이 살아난 뒤(아래 effect) 옮긴다.
   */
  const [wantsFocus, setWantsFocus] = useState(false)
  const focusInput = () => setWantsFocus(true)

  useEffect(() => {
    if (!wantsFocus) return

    // 적을 칸이 열려 있으면(방금 찍은 상자) 그쪽이 포커스를 갖는다. 요청이 끝나면서
    // 입력칸이 잠깐 disabled가 되어 blur가 나므로 이 경우의 요청은 여기서 버린다.
    if (current) {
      setWantsFocus(false)
      return
    }
    // 아직 요청 중이면 칸이 disabled 라 옮겨지지 않는다. busy 가 풀리면 이 effect 가 다시 돈다.
    if (busy) return

    setWantsFocus(false)
    inputRef.current?.focus()
  }, [wantsFocus, busy, current])

  /**
   * 운송장번호 하나를 입고 처리한다. 입력창과 전용 스캐너 앱이 함께 쓰는 유일한 길이다.
   * `fromInput`이면 입력창을 비우고 포커스를 돌려준다.
   */
  async function submitTracking(value: string, { fromInput }: { fromInput: boolean }) {
    if (!value.trim()) return

    setBusy(true)
    try {
      const res = await api.post<ScanResponse>("/api/scan", { trackingNo: value })
      // 접수된 건이 없어 무적화물로 적힌 경우. 상자는 손에 있으니 실패가 아니다 —
      // 적을 칸(중량)은 열지 않는다. 그건 접수된 건에만 있는 단계다.
      if (res.orphan) {
        setCurrent(null)
        setBanner({
          tone: "warn",
          text: t(res.created ? "scan.orphanNew" : "scan.orphanAgain", {
            trackingNo: res.orphan.trackingNo,
          }),
        })
        beep(false)
        void orphans.reload()
      } else if (res.case) {
        setCurrent(res.case)
        setBanner({ tone: "ok", text: t("scan.success") })
        beep(true)
        void today.reload()
      }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "INTERNAL"
      setCurrent(null)
      setBanner({ tone: "error", text: t(`error.${code}`) })
      beep(false)
      // 못 찾았으면 다시 찍어야 한다. 성공했으면 중량 칸이 스스로 가져간다(ArrivalEntryCard).
      if (fromInput) focusInput()
    } finally {
      setBusy(false)
      if (fromInput && inputRef.current) inputRef.current.value = ""
    }
  }

  async function scan(event: FormEvent) {
    event.preventDefault()
    await submitTracking(inputRef.current?.value ?? "", { fromInput: true })
  }

  /** 이 상자에서 적은 것(중량·착/선불·착불비)을 한 번에 기록한다. */
  async function saveArrival(save: ArrivalSave) {
    if (!current) return
    setBusy(true)
    try {
      if (save.weight) await api.post(`/api/cases/${current.id}/weight`, save.weight)
      if (save.freight) await api.post(`/api/cases/${current.id}/freight`, save.freight)
      setCurrent(null)
      void today.reload()
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "INTERNAL"
      setBanner({ tone: "error", text: t(`error.${code}`) })
    } finally {
      setBusy(false)
      focusInput()
    }
  }

  const cols = caseColumns(t, locale)
  const columns: Column<Case>[] = [
    cols.seq(),
    cols.trackingNo({ primary: true }),
    cols.orderNo({ priority: "md" }),
    cols.customerName(),
    cols.weight(),
  ]

  return (
    <div className="scan-page">
      <PageHeader title={t("scan.title")} description={t("scan.description")} />

      <Card>

        <form onSubmit={(event) => void scan(event)}>
          <Field label={t("scan.prompt")} htmlFor="scan-input">
            <Input
              id="scan-input"
              ref={inputRef}
              className="scan-input"
              size="lg"
              autoFocus
              autoComplete="off"
              aria-label={t("scan.input")}
              disabled={busy}
              enterKeyHint="go"
              // 바코드 스캐너는 번호 + Enter 를 보낸다. 포커스가 떠나면 다시 가져온다.
              // 적을 칸이 열려 있는 동안은 포커스를 가져오지 않는다.
              onBlur={() => {
                if (!current) focusInput()
              }}
            />
          </Field>
        </form>

        {/* 배너는 입력창 아래에 둔다 — 위에 있으면 스캔할 때마다 시선이 위아래로 튄다. */}
        {banner ? (
          <div className={`scan-banner scan-banner--${banner.tone}`}>{banner.text}</div>
        ) : null}

        {current ? (
          <ArrivalEntryCard
            // 상자마다 새로 그린다 — 다음 상자를 찍으면 칸이 저절로 빈다.
            key={current.id}
            item={current}
            busy={busy}
            onSave={(save) => void saveArrival(save)}
            onSkip={() => {
              setCurrent(null)
              focusInput()
            }}
          />
        ) : null}
      </Card>

      {(orphans.data?.items.length ?? 0) > 0 ? (
        <Card padded={false} className="scan-orphans">
          <CardHeader
            title={t("scan.orphanTitle")}
            icon="alert"
            description={t("scan.orphanDesc")}
            // 이은 기록까지 되짚거나 찾아보는 일은 전용 화면에서 한다. 여기 카드는
            // 찍는 중에 눈에 걸리는 "지금 처리할 상자" 만 그대로 둔다.
            actions={<Link to="/orphans">{t("scan.orphanAll")}</Link>}
          />
          <ul className="scan-orphans__list">
            {(orphans.data?.items ?? []).map((one) => (
              <li key={one.trackingNo}>
                {/* 줄 전체가 버튼이다 — 창고에서는 작은 버튼을 겨냥해 누르기 어렵다. */}
                <button
                  type="button"
                  className="scan-orphans__item"
                  onClick={() => linking.start(one)}
                >
                  <strong>{formatTracking(one.trackingNo)}</strong>
                  <span className="muted">
                    {t("scan.orphanCount", { count: one.scanCount })} · {t("orphan.link")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card padded={false} className="scan-today">
        <CardHeader
          title={t("scan.today")}
          icon="clock"
          description={t("scan.count", { count: rows.length })}
        />
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          onRowClick={(row) => detail.open(row.id)}
          empty={<EmptyState icon="scan" title={t("scan.todayEmpty")} />}
        />
      </Card>

      {linking.panel}
      {detail.panel}
    </div>
  )
}
